// src/services/eventUpgradeService.js
// Service for event tier upgrades with Cashfree payment integration

import axios from 'axios';
import supabase from './supabase';

const CLOUD_FUNCTIONS_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_URL ||
  'https://asia-south1-invits-app.cloudfunctions.net';

/**
 * Get authorization header for API calls
 */
const getAuthHeader = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Calculate GST (18% fixed)
 */
export const calculateGST = (baseAmount) => {
  const gstRate = 18;
  const gstAmount = (baseAmount * gstRate) / 100;
  return {
    base: baseAmount,
    gst: gstAmount,
    total: baseAmount + gstAmount,
    gstRate,
  };
};

/**
 * Format currency for display
 */
export const formatCurrency = (amount) => {
  return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
};

/**
 * Get all active pricing tiers by type
 * @param {string} tierType - 'host' or 'photo'
 */
export const getPricingTiers = async (tierType = 'host') => {
  try {
    const { data, error } = await supabase
      .from('event_pricing_tiers')
      .select('*')
      .eq('tier_type', tierType)
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) throw error;

    return {
      success: true,
      tiers: data || [],
    };
  } catch (error) {
    console.error('Error fetching pricing tiers:', error);
    return {
      success: false,
      error: error.message,
      tiers: [],
    };
  }
};

/**
 * Get current tier for an event
 * @param {string} eventId - Event ID
 */
export const getEventCurrentTiers = async (eventId) => {
  try {
    const { data: event, error } = await supabase
      .from('events')
      .select(`
        host_pricing_tier_id,
        photo_pricing_tier_id,
        host_tier:event_pricing_tiers!host_pricing_tier_id(
          id,
          tier_name,
          display_name,
          price
        ),
        photo_tier:event_pricing_tiers!photo_pricing_tier_id(
          id,
          tier_name,
          display_name,
          price
        )
      `)
      .eq('id', eventId)
      .single();

    if (error) throw error;

    return {
      success: true,
      hostTierId: event.host_pricing_tier_id,
      photoTierId: event.photo_pricing_tier_id,
      hostTier: event.host_tier,
      photoTier: event.photo_tier,
    };
  } catch (error) {
    console.error('Error fetching event tiers:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Check if a tier has already been purchased for an event
 * @param {string} eventId - Event ID
 * @param {string} tierId - Tier ID
 */
export const checkTierPurchased = async (eventId, tierId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('event_tier_purchases')
      .select('id, payment_status')
      .eq('event_id', eventId)
      .eq('tier_id', tierId)
      .eq('purchased_by', user.id)
      .eq('payment_status', 'completed')
      .maybeSingle();

    if (error) throw error;

    return {
      success: true,
      purchased: !!data,
      purchaseId: data?.id || null,
    };
  } catch (error) {
    console.error('Error checking tier purchase:', error);
    return {
      success: false,
      error: error.message,
      purchased: false,
    };
  }
};

/**
 * Create payment order for tier upgrade using Cashfree
 * @param {Object} orderData - Order details
 */
export const createEventTierOrder = async (orderData) => {
  try {
    const headers = await getAuthHeader();

    console.log('📤 Creating event tier upgrade order...');

    // Calculate GST
    const pricing = calculateGST(orderData.baseAmount);

    const response = await axios.post(
      `${CLOUD_FUNCTIONS_URL}/createEventTierOrderCashfree`,
      {
        eventId: orderData.eventId,
        tierId: orderData.tierId,
        tierType: orderData.tierType,
        baseAmount: pricing.base,
        gstAmount: pricing.gst,
        totalAmount: pricing.total,
        customerName: orderData.customerName,
        customerEmail: orderData.customerEmail,
        customerPhone: orderData.customerPhone,
        customerState: orderData.customerState || null,
      },
      { headers }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create order');
    }

    console.log('✅ Event tier upgrade order created');

    return {
      success: true,
      orderId: response.data.orderId,
      purchaseId: response.data.purchaseId,
      paymentSessionId: response.data.paymentSessionId,
      pricing,
    };
  } catch (error) {
    console.error('❌ Error creating event tier order:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Verify event tier payment status
 * @param {string} orderId - Cashfree order ID
 */
export const verifyEventTierPayment = async (orderId) => {
  try {
    const headers = await getAuthHeader();

    console.log('🔐 Verifying event tier payment...');

    const response = await axios.post(
      `${CLOUD_FUNCTIONS_URL}/verifyEventTierPaymentCashfree`,
      { orderId },
      { headers }
    );

    return {
      success: response.data.success,
      paymentStatus: response.data.paymentStatus,
      purchaseId: response.data.purchaseId,
      tierId: response.data.tierId,
      tierType: response.data.tierType,
      invoiceId: response.data.invoiceId,
      invoiceNumber: response.data.invoiceNumber,
      error: response.data.error,
    };
  } catch (error) {
    console.error('❌ Error verifying event tier payment:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Get user's tier purchase history
 */
export const getUserTierPurchases = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('event_tier_purchases')
      .select(`
        *,
        tier:event_pricing_tiers!tier_id(
          id,
          tier_name,
          display_name,
          tier_type,
          price
        ),
        event:events!event_id(
          id,
          title,
          cover_image_cdn_url
        )
      `)
      .eq('purchased_by', user.id)
      .order('purchased_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      purchases: data || [],
    };
  } catch (error) {
    console.error('Error fetching tier purchases:', error);
    return {
      success: false,
      error: error.message,
      purchases: [],
    };
  }
};

/**
 * Get tier purchase invoices for a user
 */
export const getUserTierInvoices = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('event_tier_invoices')
      .select(`
        *,
        event:events!event_id(
          id,
          title
        )
      `)
      .eq('user_id', user.id)
      .order('generated_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      invoices: data || [],
    };
  } catch (error) {
    console.error('Error fetching tier invoices:', error);
    return {
      success: false,
      error: error.message,
      invoices: [],
    };
  }
};

/**
 * Get invoice by ID
 */
export const getInvoiceById = async (invoiceId) => {
  try {
    const { data, error } = await supabase
      .from('event_tier_invoices')
      .select(`
        *,
        event:events!event_id(
          id,
          title
        )
      `)
      .eq('id', invoiceId)
      .single();

    if (error) throw error;

    return {
      success: true,
      invoice: data,
    };
  } catch (error) {
    console.error('Error fetching invoice:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get tier display label
 */
export const getTierDisplayLabel = (tier) => {
  if (!tier) return 'Unknown';
  return tier.display_name || tier.tier_name || 'Unknown';
};

/**
 * Get tier type display label
 */
export const getTierTypeLabel = (tierType) => {
  switch (tierType) {
    case 'host':
      return 'Host Features';
    case 'photo':
      return 'Photo Upload';
    default:
      return 'Event Upgrade';
  }
};

export default {
  calculateGST,
  formatCurrency,
  getPricingTiers,
  getEventCurrentTiers,
  checkTierPurchased,
  createEventTierOrder,
  verifyEventTierPayment,
  getUserTierPurchases,
  getUserTierInvoices,
  getInvoiceById,
  getTierDisplayLabel,
  getTierTypeLabel,
};
